import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AccessType } from "../../common/enums/DRCXEnums";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { bindDialog, BindDialogReturn, DialogProps, DialogResult, DialogState, useDialogState } from "../../hooks/use-dialog-state/UseDialogState";
import FormRadioGroup, { FormRadioGroupRadioButton } from "../forms/form-radio-group/FormRadioGroup";
import Form from "../forms/form/Form";
import ResponsiveDialog from "../responsive-dialog/ResponsiveDialog";
import UnsavedChangesPrompt from "../unsaved-changes-prompt/UnsavedChangesPrompt";

export type AccessLevelDialogInitValues = {
    access: AccessType;
    configurationType: string;
    hasGroupWrite: boolean;
    hasConfigWrite: boolean;
};

export type AccessLevelDialogResultValues = {
    access: AccessType;
};

export type AccessLevelDialogResult = DialogResult<AccessLevelDialogResultValues>;

export type AccessLevelDialogProps = DialogProps<AccessLevelDialogInitValues, AccessLevelDialogResult>;

export type AccessLevelFormValuesType = {
    access: string;
};

export default function AccessLevelDialog({
    open,
    onClose,
    values
}: AccessLevelDialogProps) {

    const { t } = useTranslation();
    const { confirm } = useConfirmation();

    const form = useForm<AccessLevelFormValuesType>({
        mode: "onChange",
        defaultValues: {
            access: `${AccessType.Private}`
        } as AccessLevelFormValuesType
    });

    const {
        handleSubmit,
        formState: {
            isDirty,
            isValid,
        },
        reset
    } = form;

    async function onSubmit(data: AccessLevelFormValuesType) {

        const dialogValues: AccessLevelDialogResultValues = {
            access: Number(data.access)
        };

        reset();

        onClose && onClose({
            reason: "ok",
            values: dialogValues
        });
    }

    async function onCancel() {
        const canCancel = isDirty
            ? await getUserExitConfirmation()
            : true;

        if (canCancel) {
            reset();
            onClose && onClose({
                reason: "cancel"
            });
        }
    }

    async function getUserExitConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    }

    useEffect(() => {

        reset({
            access: `${values?.access}`
        });
    }, [
        reset,
        values
    ]);

    const radioButtons: FormRadioGroupRadioButton[] = useMemo(() => [{
        label: t("configuration.accessLevels.private") as string,
        ariaLabel: t("configuration.accessLevels.private") as string,
        value: `${AccessType.Private}`,
        disabled: !(values?.access === AccessType.Private)
    }, {
        label: t("configuration.accessLevels.sharedReadOnly") as string,
        ariaLabel: t("configuration.accessLevels.sharedReadOnly") as string,
        value: `${AccessType.SharedReadOnly}`,
        disabled: !(values?.access === AccessType.SharedReadOnly ||
            (values?.hasGroupWrite &&
                (values?.access === AccessType.Private ||
                    (values?.access === AccessType.Shared && values.hasConfigWrite))))
    }, {
        label: t("configuration.accessLevels.shared") as string,
        ariaLabel: t("configuration.accessLevels.shared") as string,
        value: `${AccessType.Shared}`,
        disabled: !(values?.access === AccessType.Shared ||
            (values?.hasGroupWrite &&
                (values?.access === AccessType.Private ||
                    (values?.access === AccessType.SharedReadOnly && values.hasConfigWrite))))
    }], [
        values?.access,
        values?.hasConfigWrite,
        values?.hasGroupWrite,
        t
    ]);

    return (
        <>
            <ResponsiveDialog
                title={t("configuration.accessLevelDialog.title", {
                    configurationType: values?.configurationType ?? ""
                })}
                enableOkay={isDirty && isValid}
                okayLabel={t("configuration.accessLevelDialog.buttonLabel")}
                open={open}
                onClose={
                    (reason: string) => {
                        reason === "ok"
                            ? handleSubmit(async (data) => onSubmit(data))()
                            : onCancel();
                    }
                }
            >
                <Form form={form}>
                    <FormRadioGroup
                        name="access"
                        radioButtons={radioButtons}
                    />
                </Form>

            </ResponsiveDialog>

            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getUserExitConfirmation}
            />
        </>
    );
}

export type AccessLevelDialogState = DialogState<AccessLevelDialogInitValues, AccessLevelDialogResult>;
export type BindAccessLevelDialogReturn = BindDialogReturn<AccessLevelDialogInitValues, AccessLevelDialogResult>;

export function useAccessLevelDialogState(): AccessLevelDialogState {
    return useDialogState();
}

export function bindAccessLevelDialog(props: AccessLevelDialogState): BindAccessLevelDialogReturn {
    return bindDialog(props);
}
